<script>
import dayjs from 'dayjs';

const d3Scale = require('d3-scale');
const d3Shape = require('d3-shape');

const d3 = { ...d3Scale, ...d3Shape };

const HEIGHT = 300;
const padding = {
  top: 10,
  right: 10,
  bottom: 20,
  left: 20,
};

const formatDate = d => dayjs(d).format('DD.MM.YYYY');
const gtZero = d => d.hours > 0;

export default {
  props: {
    fetchHistory: {
      type: Function,
      required: true,
    },
    filter: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      isFetching: false,
      data: [],
      meta: {},

      width: 0,
    };
  },
  computed: {
    chartWidth () {
      return this.width - padding.left - padding.right;
    },
    chartHeight () {
      return HEIGHT - padding.top - padding.bottom;
    },

    xScale () {
      return d3.scaleLinear()
        .domain([-1, (this.meta.dateRange || []).length + 1])
        .range([0, this.chartWidth]);
    },
    yScale () {
      return d3.scaleLinear()
        .domain([0, this.meta.maxHours])
        .range([this.chartHeight, 0])
        .nice();
    },
    line () {
      return d3.line()
        .defined(gtZero)
        .curve(d3.curveMonotoneX)
        .x((d, i) => this.xScale(i))
        .y(d => this.yScale(d.hours));
    },
  },
  watch: {
    filter () {
      this.fetchData();
    },
  },
  methods: {
    async fetchData () {
      const response = Object.freeze(await this.fetchHistory(this.filter));

      this.data = response.data;
      this.meta = response.meta || {};
    },
  },
  async created () {
    this.isFetching = true;

    await this.fetchData();

    this.isFetching = false;
  },
  mounted () {
    const { width } = this.$el.getBoundingClientRect();

    this.width = width;
  },
  render () {
    const { $style } = this;
    const { width, chartWidth, chartHeight, xScale, yScale, line } = this;
    const { dateRange } = this.meta;

    return (
      <div style={{ height: `${HEIGHT}px` }}>
        {this.isFetching ? (
          <u-spinner />
        ) : (
          <svg
            viewBox={`0 0 ${width} ${HEIGHT}`}
            width={width}
            height={HEIGHT}
          >
            <g transform={`translate(${padding.left}, ${padding.top})`}>
              {yScale.ticks(5).map(tick => (
                <g
                  key={`${tick}-y`}
                  class={[$style.axis, $style.y]}
                  transform={`translate(0, ${yScale(tick)})`}
                >
                  <line
                    x1="0"
                    y1="0"
                    x2={chartWidth}
                    y2="0"
                  />
                  <text
                    x="-5"
                    dy=".35em"
                  >
                    {tick}
                  </text>
                </g>
              ))}
              {xScale.ticks(5).map(tick => (
                <g
                  key={`${tick}-x`}
                  class={[$style.axis, $style.x]}
                  transform={`translate(${xScale(tick)}, ${chartHeight})`}
                >
                  <line
                    x1="0"
                    y1="0"
                    x2="0"
                    y2={-chartHeight}
                  />
                  <text dy="1.35em">
                    {formatDate(dateRange[tick])}
                  </text>
                </g>
              ))}

              {this.data.map(department => (
                <g
                  key={department.id}
                  stroke={department.color}
                  class={$style.chart}
                >
                  <path
                    d={line(department.logs)}
                    fill="none"
                  />

                  <g fill="#ffffff">
                    {department.logs.map((d, i) => {
                      if (!gtZero(d)) {
                        return null;
                      }

                      return (
                        <circle
                          key={d.date}
                          cx={xScale(i)}
                          cy={yScale(d.hours)}
                          r="3"
                        >
                          <title>
                            {`${d.date}: ${d.hours}`}
                          </title>
                        </circle>
                      );
                    })}
                  </g>
                </g>
              ))}
            </g>
          </svg>
        )}
      </div>
    );
  },
};
</script>

<style lang="scss" module>
@import '@/theme/include.scss';

.axis {
  & line {
    stroke: $color-gray;
  }

  & text {
    font-size: .75em;
  }

  &.y text {
    text-anchor: end;
  }

  &.x text {
    text-anchor: middle;
  }
}

.chart {
  stroke-width: 2;
}
</style>
